import { Controller } from '@hotwired/stimulus'
import { Chart } from 'chart.js/auto'
import { lineChartOptions, barChartOptions, doughnutChartOptions } from '~/javascript/charts/chart_config'

// Chart colors following BodyOS style guide (matching prelaunch charts)
// Uses Slate (blue-gray) scale for consistency
const chartColors = {
  // Line chart colors for opened/closed tickets
  line: {
    opened: '#0f172a', // slate-900 (primary dark)
    openedFill: 'rgba(15, 23, 42, 0.1)',
    closed: '#64748b', // slate-500
    closedFill: 'rgba(100, 116, 139, 0.1)'
  },
  // Multi-item palette for category/status charts
  palette: [
    '#0f172a', // slate-900 (primary dark)
    '#334155', // slate-700
    '#64748b', // slate-500
    '#94a3b8', // slate-400
    '#1e293b', // slate-800
    '#475569' // slate-600
  ],
  // Response time trend
  trend: '#1e293b' // slate-800
}

export default class extends Controller {
  static targets = ['ticketsTimeCanvas', 'categoryCanvas', 'statusCanvas', 'responseTimeCanvas', 'loading']

  static values = {
    url: String,
    startDate: String,
    endDate: String
  }

  connect() {
    this.charts = []
    this.initializeCharts()
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy())
  }

  async initializeCharts() {
    const data = await this.fetchData()
    if (!data) return

    this.renderTicketsOverTime(data.tickets_over_time)
    this.renderTicketsByCategory(data.tickets_by_category)
    this.renderTicketsByStatus(data.tickets_by_status)
    this.renderResponseTimeTrend(data.response_time_trend)
  }

  get hasTicketsOverTimeTarget() { return this.hasTicketsTimeCanvasTarget }

  get ticketsOverTimeTarget() { return this.ticketsTimeCanvasTarget }

  get hasTicketsByCategoryTarget() { return this.hasCategoryCanvasTarget }

  get ticketsByCategoryTarget() { return this.categoryCanvasTarget }

  get hasTicketsByStatusTarget() { return this.hasStatusCanvasTarget }

  get ticketsByStatusTarget() { return this.statusCanvasTarget }

  get hasResponseTimeTrendTarget() { return this.hasResponseTimeCanvasTarget }

  get responseTimeTrendTarget() { return this.responseTimeCanvasTarget }

  async fetchData() {
    if (!this.urlValue) {
      console.error('Analytics URL not configured')
      return null
    }

    this.showLoading()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (this.startDateValue) url.searchParams.set('start_date', this.startDateValue)
      if (this.endDateValue) url.searchParams.set('end_date', this.endDateValue)

      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) {
        console.error('Analytics API error:', response.status, response.statusText)
        return null
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to fetch analytics data:', error)
      return null
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  renderTicketsOverTime(data) {
    if (!this.hasTicketsOverTimeTarget || !data) return

    const chart = new Chart(this.ticketsOverTimeTarget, {
      type: 'line',
      data: {
        labels: data.labels,
        datasets: [
          {
            label: 'Opened',
            data: data.opened,
            borderColor: chartColors.line.opened,
            backgroundColor: chartColors.line.openedFill,
            borderWidth: 2,
            fill: true,
            tension: 0
          },
          {
            label: 'Closed',
            data: data.closed,
            borderColor: chartColors.line.closed,
            backgroundColor: chartColors.line.closedFill,
            borderWidth: 2,
            fill: true,
            tension: 0
          }
        ]
      },
      options: {
        ...lineChartOptions,
        plugins: {
          ...lineChartOptions.plugins,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: false,
              boxWidth: 12,
              boxHeight: 12,
              padding: 16,
              color: '#171717',
              font: {
                family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
                size: 11
              }
            }
          }
        }
      }
    })
    this.charts.push(chart)
  }

  renderTicketsByCategory(data) {
    if (!this.hasTicketsByCategoryTarget || !data) return

    const categoryColors = data.labels.map((_, i) => chartColors.palette[i % chartColors.palette.length])

    const chart = new Chart(this.ticketsByCategoryTarget, {
      type: 'doughnut',
      data: {
        labels: data.labels,
        datasets: [{
          data: data.values,
          backgroundColor: categoryColors,
          borderColor: '#ffffff',
          borderWidth: 3
        }]
      },
      options: doughnutChartOptions
    })
    this.charts.push(chart)
  }

  renderTicketsByStatus(data) {
    if (!this.hasTicketsByStatusTarget || !data) return

    const statusColors = data.labels.map((_, i) => chartColors.palette[i % chartColors.palette.length])

    const chart = new Chart(this.ticketsByStatusTarget, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: [{
          label: 'Tickets',
          data: data.values,
          backgroundColor: statusColors,
          borderColor: '#0f172a',
          borderWidth: 2,
          borderRadius: 4
        }]
      },
      options: barChartOptions
    })
    this.charts.push(chart)
  }

  renderResponseTimeTrend(data) {
    if (!this.hasResponseTimeTrendTarget || !data) return

    const chart = new Chart(this.responseTimeTrendTarget, {
      type: 'line',
      data: {
        labels: data.labels,
        datasets: [{
          label: 'Avg Response Time (min)',
          data: data.values,
          borderColor: chartColors.trend,
          backgroundColor: 'rgba(30, 41, 59, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0
        }]
      },
      options: {
        ...lineChartOptions,
        plugins: {
          ...lineChartOptions.plugins,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: false,
              boxWidth: 12,
              boxHeight: 12,
              padding: 16,
              color: '#171717',
              font: {
                family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
                size: 11
              }
            }
          }
        }
      }
    })
    this.charts.push(chart)
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate

    this.destroyCharts()
    this.initializeCharts()
  }

  destroyCharts() {
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }
}
